import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Button,
  Spin,
  Popconfirm,
  Dropdown,
  Input,
  Badge,
  Modal,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  ReloadOutlined,
  DownOutlined,
  GoogleCircleFilled,
  EyeOutlined,
  ExportOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import styles from "./PieceJointeList.module.scss";
import Toast from "../../helpers/Toast";
import PieceJointeService from "../../services/PieceJointeService";
import { catchError } from "../../services/DaoService";
import Moment from "react-moment";
import { InlineEditableInput } from "../utils/InlineEditableInput";
import PieceJointeUploadModal from "./PieceJointeSingleForm";
import PropTypes from "prop-types";
import GoogleDrivePicker from "./GoogleDrivePicker";

const getExt = (nom = "") => nom.split(".").pop().toLowerCase();

const extIcon = (nom = "") => {
  const ext = getExt(nom);
  if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext))
    return <FileImageOutlined style={{ color: "#7c3aed" }} />;
  if (ext === "pdf") return <FilePdfOutlined style={{ color: "#dc2626" }} />;
  if (["doc", "docx"].includes(ext))
    return <FileWordOutlined style={{ color: "#1d4ed8" }} />;
  if (["xls", "xlsx"].includes(ext))
    return <FileExcelOutlined style={{ color: "#16a34a" }} />;
  if (["ppt", "pptx"].includes(ext))
    return <FilePptOutlined style={{ color: "#ea580c" }} />;
  return <FileOutlined style={{ color: "#6b7280" }} />;
};

const TEXT_EXTS = ["txt", "xml", "json", "csv", "html", "htm", "yaml", "yml", "md", "log", "sql", "js", "ts", "jsx", "tsx", "css", "scss"];
const OFFICE_EXTS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tiff", "ico"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov", "avi"];
const AUDIO_EXTS = ["mp3", "wav", "ogg", "aac", "flac", "m4a"];

const isExternalUrl = (value = "") => /^https?:\/\//i.test(value);

const openInNewTab = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

// Fetch file as blob via authenticated API and return an object URL
function useBlobUrl(uid) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const prevUrl = useRef(null);

  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    setError(false);
    PieceJointeService.getInstance()
      .getPreviewUrl(uid)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (prevUrl.current) URL.revokeObjectURL(prevUrl.current);
        prevUrl.current = url;
        setBlobUrl(url);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (prevUrl.current) URL.revokeObjectURL(prevUrl.current);
    };
  }, [uid]);

  return { blobUrl, loading, error };
}

const PreviewContent = ({ item }) => {
  // Use stored extension (from DB) — more reliable than parsing nom (which user may have renamed)
  const ext = (item.extension ?? getExt(item.nom)).toLowerCase();
  const { blobUrl, loading, error } = useBlobUrl(item.uid);
  const externalUrl = item.path ?? item.webViewLink ?? item.url;

  if (isExternalUrl(externalUrl) && !blobUrl && !loading) {
    return (
      <div className={styles.previewUnsupported}>
        <GoogleCircleFilled style={{ color: "#4285F4", fontSize: 22 }} />
        <p>Ce document est hébergé sur un service externe.</p>
        <Button
          icon={<ExportOutlined />}
          onClick={() => openInNewTab(externalUrl)}
        >
          Ouvrir dans un nouvel onglet
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.previewLoading}>
        <Spin size="large" />
        <p>Chargement du fichier…</p>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={styles.previewError}>
        <FileOutlined />
        <p>Impossible de charger le fichier.</p>
      </div>
    );
  }

  if (IMAGE_EXTS.includes(ext)) {
    return (
      <div className={styles.previewImage}>
        <img src={blobUrl} alt={item.nom} />
      </div>
    );
  }

  if (ext === "pdf") {
    return <iframe src={blobUrl} className={styles.previewPdf} title={item.nom} />;
  }

  if (VIDEO_EXTS.includes(ext)) {
    return <video src={blobUrl} controls className={styles.previewVideo} />;
  }

  if (AUDIO_EXTS.includes(ext)) {
    return (
      <div className={styles.previewAudio}>
        <audio src={blobUrl} controls />
      </div>
    );
  }

  if (TEXT_EXTS.includes(ext)) {
    return <TextPreview blobUrl={blobUrl} />;
  }

  if (OFFICE_EXTS.includes(ext)) {
    return (
      <div className={styles.previewUnsupported}>
        <FileOutlined />
        <p>Aperçu Office non disponible en mode sécurisé.</p>
        <Button icon={<ExportOutlined />} href={blobUrl} download={item.nom}>
          Télécharger pour ouvrir
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.previewUnsupported}>
      <FileOutlined />
      <p>Aperçu non disponible pour ce type de fichier.</p>
      <Button icon={<ExportOutlined />} href={blobUrl} download={item.nom}>
        Télécharger le fichier
      </Button>
    </div>
  );
};

function TextPreview({ blobUrl }) {
  const [text, setText] = useState(null);
  useEffect(() => {
    fetch(blobUrl).then(r => r.text()).then(setText).catch(() => setText("Erreur de lecture."));
  }, [blobUrl]);
  if (text === null) return <Spin />;
  return <pre className={styles.previewText}>{text}</pre>;
}

const PieceJointeList = ({
  parentType,
  parentId,
  canAdd = false,
  canRemove = false,
  canSee = false,
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isDriveModalVisible, setIsDriveModalVisible] = useState(false);
  const [isDriveLinkModalVisible, setIsDriveLinkModalVisible] = useState(false);
  const [driveLink, setDriveLink] = useState("");
  const [driveLinkName, setDriveLinkName] = useState("");
  const [isDriveLinkSubmitting, setIsDriveLinkSubmitting] = useState(false);
  const [paginationData, setPaginationData] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [previewItem, setPreviewItem] = useState(null);

  const init = useCallback(() => {
    if (!canSee) return;
    setLoading(true);
    PieceJointeService.getInstance()
      .findByParent(parentId, parentType)
      .then((response) => {
        const data = response.data;
        setAttachments(data.data);
        setPaginationData(data);
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [parentType, parentId, canSee]);

  React.useEffect(() => {
    init();
  }, [init]);

  const hasMore =
    paginationData?.next_page_url !== null && !!paginationData?.next_page_url;

  const handleLoadMore = () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    PieceJointeService.post(paginationData.next_page_url, {
      parent_type: parentType,
    })
      .then((response) => {
        setAttachments((prev) => [...prev, ...response.data.data]);
        setPaginationData(response.data);
      })
      .catch(catchError)
      .finally(() => setIsLoadingMore(false));
  };

  const handleDownload = async (item) => {
    try {
      const blob = await PieceJointeService.getInstance().getPreviewUrl(item.uid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.nom;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      catchError(err);
    }
  };

  const handleDelete = async (uid) => {
    try {
      const response = await PieceJointeService.getInstance().remove(uid);
      setAttachments((prev) => prev.filter((item) => item.uid !== uid));
      Toast.success(response?.message);
    } catch (err) {
      catchError(err);
    }
  };

  const handleFieldChange = (value, fieldName, record) => {
    const updated = { ...record, [fieldName]: value };
    setAttachments((prev) =>
      prev.map((item) => (item.uid === record.uid ? updated : item))
    );
    PieceJointeService.getInstance()
      .update(updated.uid, updated)
      .then((response) => Toast.success(response.message))
      .catch(catchError);
  };

  const extractDriveName = (url = "") => {
    try {
      const parsed = new URL(url.trim());
      const segments = parsed.pathname.split("/").filter(Boolean);
      const resourceId = segments.find((segment) =>
        /^[a-zA-Z0-9_-]{10,}$/.test(segment)
      );
      if (resourceId) {
        return `Google Drive - ${resourceId.slice(0, 8)}`;
      }
    } catch {
      // Ignore invalid URLs here; validation happens on submit.
    }
    return "Google Drive";
  };

  const openDriveLinkModal = () => {
    setDriveLink("");
    setDriveLinkName("");
    setIsDriveLinkModalVisible(true);
  };

  const handleDriveLinkChange = (value) => {
    setDriveLink(value);
    if (!driveLinkName.trim()) {
      setDriveLinkName(extractDriveName(value));
    }
  };

  const handleDriveLinkSubmit = async () => {
    const trimmedLink = driveLink.trim();
    if (!trimmedLink) {
      Toast.error("Ajoute un lien Google Drive valide.");
      return;
    }

    try {
      const parsed = new URL(trimmedLink);
      const host = parsed.hostname.toLowerCase();
      const isGoogleDriveLink =
        host === "docs.google.com" ||
        host === "drive.google.com" ||
        host.endsWith(".google.com");

      if (!isGoogleDriveLink) {
        Toast.error("Le lien doit provenir de Google Drive ou Google Docs.");
        return;
      }
    } catch {
      Toast.error("Le lien Google Drive fourni n'est pas valide.");
      return;
    }

    setIsDriveLinkSubmitting(true);
    try {
      await PieceJointeService.getInstance().storeFromDrive({
        nom: driveLinkName.trim() || extractDriveName(trimmedLink),
        path: trimmedLink,
        parent_type: parentType,
        parent_id: parentId,
      });
      Toast.success("Fichier Google Drive ajouté avec succès");
      setIsDriveLinkModalVisible(false);
      setDriveLink("");
      setDriveLinkName("");
      init();
    } catch (err) {
      catchError(err);
    } finally {
      setIsDriveLinkSubmitting(false);
    }
  };

  const uploadMenuItems = [
    {
      key: "files",
      label: "Joindre des fichiers",
      icon: <UploadOutlined />,
      onClick: () => setIsUploadModalVisible(true),
    },
    {
      key: "drive",
      label: "Google Drive",
      icon: <GoogleCircleFilled />,
      onClick: () => setIsDriveModalVisible(true),
    },
    {
      key: "drive-link",
      label: "Coller un lien Drive",
      icon: <GoogleCircleFilled />,
      onClick: openDriveLinkModal,
    },
  ];

  const filtered = attachments.filter((item) =>
    item.nom?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.list}>
      <PieceJointeUploadModal
        visible={isUploadModalVisible}
        onClose={() => setIsUploadModalVisible(false)}
        parentType={parentType}
        parentId={parentId}
        onUploadFinish={init}
      />

      <Modal
        open={isDriveModalVisible}
        onCancel={() => setIsDriveModalVisible(false)}
        footer={null}
        title="Google Drive"
        width={600}
        destroyOnClose
      >
        <GoogleDrivePicker
          parentType={parentType}
          parentId={parentId}
          onUploadFinish={() => {
            init();
            setIsDriveModalVisible(false);
          }}
        />
      </Modal>

      <Modal
        open={isDriveLinkModalVisible}
        onCancel={() => setIsDriveLinkModalVisible(false)}
        onOk={handleDriveLinkSubmit}
        okText="Importer"
        confirmLoading={isDriveLinkSubmitting}
        title="Coller un lien Google Drive"
        destroyOnClose
      >
        <div style={{ display: "grid", gap: 12 }}>
          <Input.TextArea
            value={driveLink}
            onChange={(e) => handleDriveLinkChange(e.target.value)}
            placeholder="https://docs.google.com/document/d/..."
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          <Input
            value={driveLinkName}
            onChange={(e) => setDriveLinkName(e.target.value)}
            placeholder="Nom à afficher dans la liste"
            allowClear
          />
        </div>
      </Modal>

      <Modal
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        title={previewItem?.nom}
        width="min(92vw, 960px)"
        centered
        footer={null}
        destroyOnClose
      >
        {previewItem && <PreviewContent item={previewItem} />}
      </Modal>

      <div className={styles.toolbar}>
        <Badge
          count={attachments.length}
          showZero
          color="#6b7280"
          className={styles.count}
        />
        <Input
          placeholder="Rechercher..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className={styles.search}
        />
        <div className={styles.actions}>
          {canAdd && (
            <Dropdown
              menu={{ items: uploadMenuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button size="small" icon={<UploadOutlined />}>
                Ajouter <DownOutlined />
              </Button>
            </Dropdown>
          )}
          <Button icon={<ReloadOutlined />} size="small" onClick={init} />
        </div>
      </div>

      <Spin spinning={loading}>
        <div>
          {filtered.map((item) => (
            <div key={item.uid} className={styles.row}>
              <div className={styles.rowIcon}>
                {item.drive ? (
                  <GoogleCircleFilled style={{ color: "#4285F4", fontSize: 20 }} />
                ) : (
                  extIcon(item.nom)
                )}
              </div>
              <div className={styles.rowBody}>
                <div className={styles.rowName}>
                  <InlineEditableInput
                    canEdit={canAdd && item.editable}
                    record={item}
                    value={item.nom}
                    fieldName="nom"
                    onChange={handleFieldChange}
                  >
                    <button
                      type="button"
                      className={styles.previewLinkButton}
                      onClick={() => setPreviewItem(item)}
                    >
                      {item.nom}
                    </button>
                  </InlineEditableInput>
                </div>
                {item.description && (
                  <div className={styles.rowDesc}>
                    <InlineEditableInput
                      canEdit={canAdd && item.editable}
                      type="textarea"
                      record={item}
                      value={item.description}
                      fieldName="description"
                      onChange={handleFieldChange}
                    >
                      <span>{item.description}</span>
                    </InlineEditableInput>
                  </div>
                )}
                <div className={styles.rowMeta}>
                  <Moment fromNow>{item.created_at}</Moment>
                </div>
              </div>
              <div className={styles.rowActions}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => setPreviewItem(item)}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<ExportOutlined />}
                  onClick={() => handleDownload(item)}
                />
                {canRemove && (
                  <Popconfirm
                    title="Supprimer ce document ?"
                    okType="danger"
                    okText="Supprimer"
                    cancelText="Annuler"
                    onConfirm={() => handleDelete(item.uid)}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                )}
              </div>
            </div>
          ))}

          {filtered.length === 0 && !loading && (
            <div className={styles.empty}>
              <InboxOutlined style={{ fontSize: 32, color: "#d1d5db" }} />
              <p>Aucun document</p>
            </div>
          )}
        </div>

        {hasMore && (
          <div className={styles.loadMore}>
            <Button
              size="small"
              icon={<DownOutlined />}
              loading={isLoadingMore}
              onClick={handleLoadMore}
            >
              Charger plus
            </Button>
          </div>
        )}
      </Spin>
    </div>
  );
};

PieceJointeList.propTypes = {
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  canAdd: PropTypes.bool,
  canRemove: PropTypes.bool,
  canSee: PropTypes.bool,
};

PreviewContent.propTypes = {
  item: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    nom: PropTypes.string.isRequired,
    extension: PropTypes.string,
    path: PropTypes.string,
    webViewLink: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
};

TextPreview.propTypes = {
  blobUrl: PropTypes.string.isRequired,
};

export default PieceJointeList;
